import { Line } from '@react-three/drei';
import React, { memo, useMemo } from 'react';
import * as THREE from 'three';

import { MeasureKind, UnitsByMeasure } from 'convert';
import { useIntl } from 'react-intl';
import { WallLengthIndicatorProps } from '../../../@types/Components';
import { EUnitType } from '../../../@types/Enums';
import { useLanguage } from '../../../provider/LanguageProvider';
import { useMultiSelectToggleContext } from '../../../provider/MultiSelectToggleProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import {
  DEFAULT_INDICATOR_COLOR,
  DEFAULT_INDICATOR_FONT_SIZE,
  DEFAULT_INDICATOR_LINE_WIDTH,
  DEFAULT_WALL_INNER_OFFSET,
  INNER_WALL_OFFSET_ADJUSTED,
  WALL_TEXT_OFFSET
} from '../../../utils/constants';
import { convertTo, getLocalizedUnitLabel } from '../../../utils/conversionUtils';
import { localizeNumber } from '../../../utils/numberUtils';
import RENDER_ORDERS from '../../../utils/renderOrder';
import RoundedBoxWithText from '../RoundedBoxWithText';

interface ExtendedIndicatorProps extends WallLengthIndicatorProps {
  polygonOrientation?: number;
}

const InnerWallLengthIndicator = ({
  startPoint,
  endPoint,
  wallSideALength,
  color = DEFAULT_INDICATOR_COLOR,
  lineWidth = DEFAULT_INDICATOR_LINE_WIDTH,
  shiftIndicator = false,
  isWallSelected = false,
  showLine = true,
  wallSide = 'A',
  polygonOrientation = 1,
}: ExtendedIndicatorProps): React.JSX.Element => {
  const { showWallInnerLength } = useMultiSelectToggleContext();
  const { unitType } = useUnitContext();
  const { locale } = useLanguage();
  const intl = useIntl();
  const mLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'meter'), [intl]);
  const ftLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'foot'), [intl]);

  const toVector3 = (point: number[] | THREE.Vector3): THREE.Vector3 => {
    if ((point as THREE.Vector3).isVector3) {
      return point as THREE.Vector3;
    }
    return new THREE.Vector3((point as number[])[0], (point as number[])[1], 0);
  };

  const pointA = useMemo(() => toVector3(startPoint), [startPoint]);
  const pointB = useMemo(() => toVector3(endPoint), [endPoint]);
  const vectorAB = useMemo(() => new THREE.Vector3().subVectors(pointB, pointA), [pointA, pointB]);

  // FIX: Die berechnung der Normale MUSS die globale Raumorientierung beachten!
  const perpendicular = useMemo(() => {
    if (polygonOrientation === 1) {
      // CCW (Gegen den Uhrzeigersinn) -> Normale nach Links
      return new THREE.Vector3(-vectorAB.y, vectorAB.x, 0).normalize();
    }
    // CW (Im Uhrzeigersinn) -> Normale nach Rechts
    return new THREE.Vector3(vectorAB.y, -vectorAB.x, 0).normalize();
  }, [vectorAB, polygonOrientation]);

  const adjustedInnerOffset = shiftIndicator ? INNER_WALL_OFFSET_ADJUSTED : DEFAULT_WALL_INNER_OFFSET;
  const innerOffset = useMemo(() => perpendicular.clone().multiplyScalar(adjustedInnerOffset), [adjustedInnerOffset, perpendicular]);
  const pointInnerC = useMemo(() => pointA.clone().add(innerOffset), [pointA, innerOffset]);
  const pointInnerD = useMemo(() => pointB.clone().add(innerOffset), [pointB, innerOffset]);

  const midPointInner = useMemo(() => {
    const textPerpendInner = perpendicular.clone().multiplyScalar(WALL_TEXT_OFFSET);
    const aboveInnerC = pointInnerC.clone().add(textPerpendInner);
    const aboveInnerD = pointInnerD.clone().add(textPerpendInner);
    return new THREE.Vector3((aboveInnerC.x + aboveInnerD.x) / 2, (aboveInnerC.y + aboveInnerD.y) / 2, 1);
  }, [pointInnerC, pointInnerD, perpendicular]);

  const sideAlengthText = useMemo(() => {
    if (!wallSideALength) return '';
    const value =
      unitType === EUnitType.METRIC
        ? localizeNumber(wallSideALength.toFixed(2), locale) + mLocalizedLabel
        : localizeNumber(convertTo(wallSideALength, 'meters' as UnitsByMeasure<MeasureKind.Length>, 'feet' as UnitsByMeasure<MeasureKind.Length>).toFixed(2), locale) +
          ftLocalizedLabel;

    const sideLabel = wallSide === 'A' ? 'L1' : 'L2';

    return isWallSelected ? `${sideLabel}: ${value}` : value;
  }, [wallSideALength, unitType, locale, isWallSelected, mLocalizedLabel, ftLocalizedLabel, wallSide]);

  const boxSize = useMemo(() => (isWallSelected ? [3.9, 1.2, 0] : [2.9, 1.2, 0]), [isWallSelected]);

  return (
    <group name={'group-wallLengthIndicator'} renderOrder={RENDER_ORDERS.lengthIndicator}>
      {showWallInnerLength && (
        <>
          {showLine && (
            <>
              <Line name={'innerWallLengthIndicator-line'} points={[pointInnerC, pointInnerD]} color={color} lineWidth={lineWidth} transparent={true} />
              <Line name={'innerWallLengthIndicator-line'} points={[pointA, pointInnerC]} color={color} lineWidth={lineWidth} transparent={true} />
              <Line name={'innerWallLengthIndicator-line'} points={[pointB, pointInnerD]} color={color} lineWidth={lineWidth} transparent={true} />
            </>
          )}
          <RoundedBoxWithText position={midPointInner} boxSize={boxSize} fontSize={DEFAULT_INDICATOR_FONT_SIZE} textColor="black" text={sideAlengthText} />
        </>
      )}
    </group>
  );
};

export default memo(InnerWallLengthIndicator);
